import type * as JsZipNs from 'jszip';
import { dispatch, type DispatchValue } from '../pipeline/dispatch';
import { redactDispatch, defaultSelection } from './redact';
import type { ReviewSelection } from '../ui/types';

export type BatchEntryState =
  | { kind: 'pending' }
  | { kind: 'dispatching' }
  | { kind: 'ready'; value: DispatchValue }
  | { kind: 'redacting' }
  | { kind: 'done'; outputName: string }
  | { kind: 'error'; message: string };

export type BatchEntry = {
  /** Stable identifier; survives across UI re-renders. */
  id: string;
  file: File;
  state: BatchEntryState;
  /** Per-entry selection, set after dispatch resolves. */
  selection?: ReviewSelection;
};

const newId = ((): (() => string) => {
  let n = 0;
  return () => {
    n += 1;
    return `batch-${String(n)}`;
  };
})();

const extOf = (mime: string, fallback: string): string => {
  if (mime === 'text/plain') return 'txt';
  if (mime === 'text/csv') return 'csv';
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'application/pdf') return 'pdf';
  return fallback;
};

/**
 * Stable per-entry filename inside the batch zip. Per CLAUDE.md, the
 * original filename is never preserved.
 */
export const batchOutputName = (index: number, outputMime: string, originalExt: string): string =>
  `redacted-${String(index + 1)}.${extOf(outputMime, originalExt)}`;

export type BatchProgress = (entries: readonly BatchEntry[]) => void;

/**
 * Build initial entries from a file list. Doesn't start dispatch — call
 * `dispatchAll` when you're ready.
 */
export const createBatch = (files: readonly File[]): BatchEntry[] =>
  files.map((file) => ({
    id: newId(),
    file,
    state: { kind: 'pending' as const },
  }));

/**
 * Dispatch every pending entry sequentially (predictable memory pressure).
 * Calls `onProgress` after each entry resolves so the UI can re-render.
 */
export const dispatchAll = async (
  entries: BatchEntry[],
  onProgress: BatchProgress,
): Promise<void> => {
  for (const entry of entries) {
    if (entry.state.kind !== 'pending') continue;
    entry.state = { kind: 'dispatching' };
    onProgress(entries);
    const result = await dispatch(entry.file);
    if (result.ok) {
      entry.state = { kind: 'ready', value: result.value };
      entry.selection = defaultSelection(result.value);
    } else {
      entry.state = { kind: 'error', message: result.error.message };
    }
    onProgress(entries);
  }
};

type JsZipCtor = typeof JsZipNs;

const loadJsZip = async (): Promise<JsZipCtor> => {
  const mod = await import('jszip');
  return (mod as unknown as { default?: JsZipCtor }).default ?? (mod as unknown as JsZipCtor);
};

export type BatchZipResult = {
  blob: Blob;
  outputName: string;
  /** Number of entries that were successfully redacted. */
  redactedCount: number;
};

/**
 * Run the redactor for each ready entry, collect blobs into a zip, and
 * return the final blob with a stable name.
 */
export const redactAndZip = async (
  entries: BatchEntry[],
  onProgress: BatchProgress,
): Promise<BatchZipResult> => {
  const JSZip = await loadJsZip();
  const zip = new JSZip();
  let redactedCount = 0;

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry?.state.kind !== 'ready' || !entry.selection) continue;
    const value = entry.state.value;
    const selection = entry.selection;
    entry.state = { kind: 'redacting' };
    onProgress(entries);
    try {
      const out = await redactDispatch(value, selection);
      const originalExt = entry.file.name.split('.').pop() ?? 'bin';
      const name = batchOutputName(i, out.mime, originalExt);
      zip.file(name, out.blob);
      entry.state = { kind: 'done', outputName: name };
      redactedCount += 1;
    } catch (err) {
      entry.state = {
        kind: 'error',
        message: err instanceof Error ? err.message : 'redaction failed',
      };
    }
    onProgress(entries);
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  return { blob, outputName: 'anomia-redacted.zip', redactedCount };
};
